import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  schema?: Record<string, unknown>;
}

export default function SEO({ title, description, keywords, canonicalUrl, ogType = 'website', ogImage, schema }: SEOProps) {
  useEffect(() => {
    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    document.title = title;
    setMetaTag('description', description);
    setMetaTag('keywords', keywords);

    const basePath = __BASE_PATH__;
    const fullUrl = canonicalUrl
      ? (canonicalUrl.startsWith('http') ? canonicalUrl : `${window.location.origin}${basePath}${canonicalUrl}`)
      : window.location.href;

    setMetaTag('og:title', title, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:type', ogType, true);
    setMetaTag('og:url', fullUrl, true);
    if (ogImage) setMetaTag('og:image', ogImage, true);

    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', title);
    setMetaTag('twitter:description', description);

    const existingLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (existingLink) {
      existingLink.href = fullUrl;
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = fullUrl;
      document.head.appendChild(link);
    }

    if (schema) {
      const existingScript = document.getElementById('structured-data');
      if (existingScript) existingScript.remove();
      const script = document.createElement('script');
      script.id = 'structured-data';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }

    return () => {
      if (schema) {
        const s = document.getElementById('structured-data');
        if (s) s.remove();
      }
    };
  }, [title, description, keywords, canonicalUrl, ogType, ogImage, schema]);

  return null;
}